<script>
  import '@awesome.me/webawesome-pro/dist/components/dialog/dialog.js';
  import '@awesome.me/webawesome-pro/dist/components/button/button.js';
  import '@awesome.me/webawesome-pro/dist/components/badge/badge.js';
  import LoadSpinner from '#components/LoadSpinner';
  import { getCategoryName } from '#state/categories';
  import { getFlag, apiFetch } from '#state/auth';

  // assetId is now an internal state rather than a static prop
  let assetId = $state(null);

  let dialog = $state(null);
  let isOpen = $state(false);
  let currentDetail = $state(null);
  let detailPromise = $state(null);

  // Derived state to safely check if URLs exist, re-evaluating anytime currentDetail changes
  let hasBrowseUrl = $derived(
    currentDetail !== null &&
    currentDetail.browse_url !== null &&
    currentDetail.browse_url !== undefined &&
    currentDetail.browse_url !== ''
  );

  let hasDownloadUrl = $derived(
    currentDetail !== null &&
    currentDetail.download_url !== null &&
    currentDetail.download_url !== undefined &&
    currentDetail.download_url !== ''
  );

  export function show(newId) {
    assetId = newId;
    isOpen = true;
    currentDetail = null; // Clear previous state so the footer doesn't flash old buttons

    // Because this is a singleton, we MUST trigger a fresh fetch every time it opens
    detailPromise = fetchDetails();

    detailPromise.then(item => {
      currentDetail = item;
    }).catch(err => {
      console.error('Modal Fetch Error:', err);
    });

    // Give Svelte a microtask to flush the {#if} blocks to the DOM
    // before the Web Component opens and attempts to measure its named slots.
    setTimeout(() => {
      if (dialog !== null) {
        dialog.open = true;
        window.history.replaceState(null, '', '#' + assetId);
      }
    }, 10);
  }

  // Triggers when programmatically closed OR closed by Esc/Backdrop clicks
  function handleDialogHide() {
    isOpen = false;
    if (window.location.hash === '#' + assetId) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  export function hide() {
    if (dialog !== null) {
      dialog.open = false;
    }
  }

  async function fetchDetails() {
    const res = await apiFetch(`/asset/${assetId}`);
    if (res.ok === false) {
      throw new Error(`Failed to load asset details: ${res.statusText}`);
    }

    const data = await res.json();
    let item = data;

    // Safely extract from nested envelope or array if present
    if (data.result !== undefined && data.result !== null) {
      if (Array.isArray(data.result) === true) {
        item = data.result[0];
      } else {
        item = data.result;
      }
    }

    // Dynamic debug check via the auth state
    if (getFlag('debug_level', 0) >= 1) {
      const lorem = "\n\nLorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.".repeat(3);
      item.description = (item.description || 'No description available.') + lorem;
      item.changelog = (item.changelog || 'No changelog available.') + lorem;
    }

    return item;
  }

  function handleCloseClick() {
    hide();
  }

  function handleImageError(e) {
    if (e !== null && e.target !== null) {
      e.target.style.display = 'none';
    }
  }
</script>

<wa-dialog bind:this={dialog} label="Asset Details" without-header onwa-hide={handleDialogHide}>
  {#if isOpen === true}
    <LoadSpinner loader={() => detailPromise}>
      {#snippet children(detail)}
        <div class="modal-content">
          <div class="header-section">
            <div class="icon-container">
              {#if detail.icon_url !== null && detail.icon_url !== undefined && detail.icon_url !== ''}
                <img
                  src={detail.icon_url}
                  alt={detail.title}
                  class="asset-icon"
                  onerror={handleImageError}
                />
              {:else}
                <div class="icon-placeholder"></div>
              {/if}
            </div>

            <div class="title-info">
              <h2>{detail.title}</h2>
              <p class="author">By {detail.author || 'Unknown'}</p>

              <div class="badges-row">
                {#if detail.godot_version !== null && detail.godot_version !== undefined}
                  <wa-badge variant="primary" size="small">Godot {detail.godot_version}</wa-badge>
                {/if}
                {#if detail.category_id !== null && detail.category_id !== undefined}
                  <wa-badge variant="neutral" size="small">{getCategoryName(detail.category_id)}</wa-badge>
                {/if}
                {#if detail.cost !== null && detail.cost !== undefined && detail.cost !== ''}
                  <wa-badge variant="success" size="small">{detail.cost}</wa-badge>
                {/if}
              </div>
            </div>
          </div>

          <div class="meta-section">
            <div class="meta-item">
              <strong>Version:</strong> {detail.version_string || detail.version || '0.0.0'}
            </div>
            <div class="meta-item">
              <strong>Last Modified:</strong> {detail.modify_date !== null && detail.modify_date !== undefined ? detail.modify_date.split(' ')[0] : 'Unknown'}
            </div>
            <div class="meta-item">
              <strong>Support:</strong> {detail.support_level || 'Unknown'}
            </div>
          </div>

          <div class="text-section">
            <h3>Description</h3>
            <p>{detail.description || 'No description available.'}</p>
          </div>

          {#if detail.changelog !== null && detail.changelog !== undefined && detail.changelog !== ''}
            <div class="text-section">
              <h3>Changelog</h3>
              <p>{detail.changelog}</p>
            </div>
          {/if}
        </div>
      {/snippet}
    </LoadSpinner>

    <div slot="footer" class="modal-footer">
      <div class="left-actions">

        <wa-button
          variant="text"
          href={hasBrowseUrl === true ? currentDetail.browse_url : undefined}
          target="_blank"
          disabled={hasBrowseUrl === false}
        >
          View Repository
        </wa-button>

        <wa-button
          variant="primary"
          href={hasDownloadUrl === true ? currentDetail.download_url : undefined}
          target="_blank"
          disabled={hasDownloadUrl === false}
        >
          Download Asset
        </wa-button>

      </div>

      <div class="spacer"></div>

      <wa-button variant="neutral" outline onclick={handleCloseClick}>Close</wa-button>
    </div>
  {/if}
</wa-dialog>

<style>
  wa-dialog {
    --width: 50vw;
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .header-section {
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;
  }

  .icon-container {
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
    border-radius: var(--wa-border-radius-m);
    overflow: hidden;
    background-color: var(--wa-color-neutral-100);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-placeholder {
    width: 100%;
    height: 100%;
    background-color: var(--wa-color-neutral-200);
  }

  .asset-icon {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .title-info {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .title-info h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .author {
    margin: 0;
    font-size: 0.95rem;
    color: var(--wa-color-neutral-600);
    font-weight: 500;
  }

  .badges-row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.25rem;
  }

  .meta-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    background-color: var(--wa-color-surface-raised);
    padding: 1rem;
    border-radius: var(--wa-border-radius-m);
    font-size: 0.9rem;
  }

  .meta-item strong {
    display: block;
    color: var(--wa-color-neutral-600);
    margin-bottom: 0.25rem;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .text-section h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
    font-weight: 600;
    border-bottom: 1px solid var(--wa-color-neutral-200);
    padding-bottom: 0.25rem;
  }

  .text-section p {
    margin: 0;
    line-height: 1.6;
    color: var(--wa-color-neutral-800);
    white-space: pre-wrap;
    font-size: 0.95rem;
  }

  .modal-footer {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--wa-color-neutral-200);
    width: 100%;
  }

  .left-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .spacer {
    flex-grow: 1;
  }
</style>